/**
 * Copyright (c) Simam Digital. Built on Project Flowerbed
 * (Copyright (c) Meta Platforms, Inc. and affiliates).
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { GameStateComponent } from '../../components/GameStateComponent';
import { LOCOMOTION_CONSTANTS } from '../../Constants';
import { PlayerStateComponent } from '../../components/PlayerStateComponent';
import { SharedWorldComponent } from '../../components/MultiplayerComponents';
import { System } from 'ecsy';
import { getOnlyEntity } from '../../utils/entityUtils';
import { plotSpawnPosition } from '../../lib/world/plotGrid';

/**
 * Places the player in their own plot cell within the shared-world grid, and (in a
 * later phase) warps them to a neighbour's cell to visit.
 *
 * On the first frame the shared world is ready (identity + plotIndex known), the
 * player is anchored to their plot's spawn point — the map's start position shifted
 * into their cell (plotOffset). Cell 0 resolves to the map's own start, so a
 * plotIndex of 0 (and single-player) spawns exactly where it always did.
 *
 * Dormant unless the shared world is configured + ready.
 */
export class PlotGridSystem extends System {
	init() {
		this._anchored = false;
	}

	execute() {
		if (this._anchored) return;

		const swEntity = getOnlyEntity(this.queries.sharedWorld, false);
		const sharedWorld = swEntity
			? swEntity.getComponent(SharedWorldComponent)
			: null;
		if (!sharedWorld || !sharedWorld.configured || !sharedWorld.ready) return;
		if (sharedWorld.plotIndex < 0) return;

		const playerEntity = getOnlyEntity(this.queries.player, false);
		const gameManager = getOnlyEntity(this.queries.gameManager, false);
		if (!playerEntity || !gameManager) return;

		const viewerTransform = playerEntity.getComponent(PlayerStateComponent)
			.viewerTransform;
		if (!viewerTransform) return;

		const gameState = gameManager.getComponent(GameStateComponent);
		const base = LOCOMOTION_CONSTANTS.INITIAL_POSITION[gameState.currentBaseMapId];
		if (!base) return;

		// Anchor the player into their own cell (cell 0 == the map's own start).
		const spawn = plotSpawnPosition(sharedWorld.plotIndex, base);
		viewerTransform.position.set(spawn.x, spawn.y, spawn.z);
		const playerState = playerEntity.getMutableComponent(PlayerStateComponent);
		playerState.didJustTeleport = true;
		this._anchored = true;

		console.info(
			`[PlotGrid] Anchored player to plot ${sharedWorld.plotIndex} at ` +
				`(${spawn.x.toFixed(1)}, ${spawn.z.toFixed(1)}).`,
		);
	}
}

PlotGridSystem.queries = {
	sharedWorld: { components: [SharedWorldComponent] },
	player: { components: [PlayerStateComponent] },
	gameManager: { components: [GameStateComponent] },
};
